import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from './ui/alert-dialog';
import { toast } from 'sonner';

/**
 * Reusable delete button with confirmation.
 * Props:
 *  - onConfirm: async () => void
 *  - title, description: dialog text
 *  - disabled: boolean
 *  - size, variant: forwarded to Button
 */
export const DeleteButton = ({
  onConfirm, title = 'Delete this item?',
  description = 'This action cannot be undone.',
  disabled = false, label, iconOnly = true,
}) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try { await onConfirm(); } catch (e) { toast.error('Delete failed'); } finally { setBusy(false); }
  };
  if (disabled) return null;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {iconOnly ? (
          <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" title="Delete">
            <Icons.Trash2 className="w-4 h-4" />
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50">
            <Icons.Trash2 className="w-4 h-4 mr-1.5" /> {label || 'Delete'}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-rose-600 hover:bg-rose-700"
            disabled={busy}
            onClick={handle}
          >
            {busy ? 'Deleting...' : 'Yes, delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteButton;
